/**
 * The brief's versions (slice 6d spec §8.5, desktop only): Recent changes in
 * the side panel, and All N versions, each with its author, time, the lines
 * it took out and put in, and Undo.
 */
import { Button } from "@crew/ui/components/button";
import { Spinner } from "@crew/ui/components/spinner";
import { Eyebrow } from "@crew/ui/components/typography";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import { useBriefVersions } from "./brief-api";
import { authorLabel, changeSummary, plainLine, versionList, type BriefChange } from "./brief";

type Undo = { run: (version: number) => void; busy: boolean };

function UndoButton({ change, undo }: { change: BriefChange; undo: Undo }) {
  if (!change.can_undo) return null;
  return (
    <Button
      variant="link"
      size="xs"
      className="h-auto shrink-0 px-0 text-foreground"
      disabled={undo.busy}
      onClick={() => undo.run(change.version)}
      data-brief-undo={change.version}
    >
      Undo
    </Button>
  );
}

const byLine = (change: Pick<BriefChange, "updated_by" | "updated_at">, me: Me) =>
  `${authorLabel(change.updated_by, me.user.id)} · ${shortTime(change.updated_at, me.workspace.timezone)}`;

/** The last few versions, and All N versions. */
export function RecentChanges({
  recent,
  version,
  me,
  undo,
  onAll,
}: {
  recent: BriefChange[];
  version: number;
  me: Me;
  undo: Undo;
  onAll: () => void;
}) {
  return (
    <section
      className="flex flex-col gap-3 rounded-xl bg-card px-5 py-4 ring-1 ring-border"
      data-brief-recent
    >
      <Eyebrow>Recent changes</Eyebrow>
      {recent.length === 0 ? (
        <p className="text-meta text-muted-foreground">Nothing changed this week.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {recent.slice(0, 3).map((change) => (
            <li key={change.version} className="flex items-start gap-3 py-2.5">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-body text-foreground">{changeSummary(change.lines)}</span>
                <span className="text-meta text-muted-foreground">{byLine(change, me)}</span>
              </span>
              <UndoButton change={change} undo={undo} />
            </li>
          ))}
        </ul>
      )}
      {version > 1 ? (
        <Button variant="link" size="xs" className="h-auto self-start px-0" onClick={onAll}>
          All {version} versions
        </Button>
      ) : null}
    </section>
  );
}

function Diff({ lines }: { lines: BriefChange["lines"] }) {
  return (
    <div className="flex flex-col gap-0.5 font-mono text-xs leading-5">
      {lines.removed.map((l, i) => (
        <p key={`-${i}`} className="text-brick line-through decoration-brick/40">
          − {plainLine(l)}
        </p>
      ))}
      {lines.added.map((l, i) => (
        <p key={`+${i}`} className="text-moss">
          + {plainLine(l)}
        </p>
      ))}
    </div>
  );
}

/** All N versions: newest first, 30 a page, the first version last. */
export function VersionsView({
  areaId,
  me,
  undo,
  onBack,
}: {
  areaId: string;
  me: Me;
  undo: Undo;
  onBack: () => void;
}) {
  const pages = useBriefVersions(areaId, true);
  const { versions, first } = versionList(pages.data?.pages, pages.hasNextPage);
  return (
    <div className="flex flex-col gap-4" data-brief-versions>
      <div className="flex items-baseline justify-between">
        <h2 className="text-lead font-semibold text-foreground">Versions</h2>
        <Button variant="link" size="xs" className="h-auto px-0" onClick={onBack}>
          Back to the brief
        </Button>
      </div>
      <PageState error={pages.error} pending={pages.isPending} />
      <ul className="flex flex-col divide-y divide-border rounded-xl bg-card ring-1 ring-border">
        {versions.map((v) => (
          <VersionRow key={v.version} change={v} me={me} undo={undo} />
        ))}
        {first ? <FirstRow first={first} me={me} /> : null}
      </ul>
      {pages.hasNextPage ? (
        <Button
          variant="outline"
          className="self-start"
          disabled={pages.isFetchingNextPage}
          onClick={() => void pages.fetchNextPage()}
        >
          Older versions
        </Button>
      ) : null}
    </div>
  );
}

function PageState({ error, pending }: { error: unknown; pending: boolean }) {
  if (error) return <p className="text-meta text-brick">{errorHint(error)}</p>;
  return pending ? <Spinner className="size-5 text-muted-foreground" /> : null;
}

function VersionRow({ change, me, undo }: { change: BriefChange; me: Me; undo: Undo }) {
  return (
    <li className="flex items-start gap-4 px-5 py-3.5" data-version={change.version}>
      <span className="w-10 shrink-0 text-meta text-muted-foreground tabular">
        v{change.version}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-meta text-muted-foreground">{byLine(change, me)}</span>
        <Diff lines={change.lines} />
      </span>
      <UndoButton change={change} undo={undo} />
    </li>
  );
}

function FirstRow({
  first,
  me,
}: {
  first: Pick<BriefChange, "version" | "updated_by" | "updated_at">;
  me: Me;
}) {
  return (
    <li className="flex items-start gap-4 px-5 py-3.5">
      <span className="w-10 shrink-0 text-meta text-muted-foreground tabular">
        v{first.version}
      </span>
      <span className="text-meta text-muted-foreground">Made · {byLine(first, me)}</span>
    </li>
  );
}
